import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BarberReportRow,
  CashMovementDto,
  CashRegisterDetailDto,
  CashRegisterDto,
  CommissionListDto,
  CommissionRuleDto,
  DailyReportPoint,
  ExpenseDto,
  FinancialSummary,
  PackageOptionDto,
  Paginated,
  PaymentDto,
  PaymentInput,
  PaymentMethodDto,
  PaymentMethodReportRow,
  PaymentQuoteDto,
  PendingPaymentDto,
  SubscriptionPaymentInfoDto,
  TopServiceRow,
} from '@barbeiropro/contracts';
import { api } from '@/lib/api';

type Q = Record<string, string | number | boolean | null | undefined>;
const FIN = 'finance';

function useList<T>(path: string, key: string, query: Q, enabled = true) {
  return useQuery({
    queryKey: [FIN, key, query],
    queryFn: () => api.get<Paginated<T>>(path, query),
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** Mutação que invalida todas as consultas do financeiro ao concluir. */
function useFinMutation<I, O>(fn: (input: I) => Promise<O>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: [FIN] }) });
}

// ---------- Caixa ----------
export const usePaymentMethods = () =>
  useQuery({ queryKey: [FIN, 'methods'], queryFn: () => api.get<PaymentMethodDto[]>('/payment-methods'), staleTime: 300_000 });

export const useCurrentRegister = (branchId: string | null | undefined) =>
  useQuery({
    queryKey: [FIN, 'register', 'current', branchId],
    queryFn: () => api.get<{ register: CashRegisterDetailDto | null }>('/cash-registers/current', { branchId }),
    enabled: !!branchId,
    refetchInterval: 60_000,
  });

export const useRegisterDetail = (id: string | null) =>
  useQuery({ queryKey: [FIN, 'register', id], queryFn: () => api.get<CashRegisterDetailDto>(`/cash-registers/${id}`), enabled: !!id });

export const useRegisters = (query: Q, enabled = true) => useList<CashRegisterDto>('/cash-registers', 'registers', query, enabled);
export const usePendingPayments = (query: Q, enabled = true) => useList<PendingPaymentDto>('/payments/pending', 'pending', query, enabled);
export const usePayments = (query: Q, enabled = true) => useList<PaymentDto>('/payments', 'payments', query, enabled);

export const useOpenRegister = () =>
  useFinMutation((b: { branchId: string; openingAmountCents: number; notes?: string }) => api.post<CashRegisterDetailDto>('/cash-registers', b));
export const useAddMovement = (registerId: string) =>
  useFinMutation((b: { type: 'withdrawal' | 'deposit' | 'adjustment'; amountCents: number; reason: string }) =>
    api.post<CashMovementDto>(`/cash-registers/${registerId}/movements`, b),
  );
export const useCloseRegister = (registerId: string) =>
  useFinMutation((b: { countedAmountCents: number; notes?: string }) => api.post<CashRegisterDetailDto>(`/cash-registers/${registerId}/close`, b));
export const useRefundPayment = () =>
  useFinMutation((b: { id: string; reason: string }) => api.post<PaymentDto>(`/payments/${b.id}/refund`, { reason: b.reason }));

// ---------- Checkout ----------
export const useCreatePayment = () => useFinMutation((b: PaymentInput) => api.post<PaymentDto>('/payments', b));

export const usePaymentQuote = (
  params: { appointmentId?: string | null; subscriptionId?: string | null; couponCode?: string; usePackageSubscriptionId?: string },
  enabled: boolean,
) =>
  useQuery({
    queryKey: [FIN, 'quote', params],
    queryFn: () => api.post<PaymentQuoteDto>('/payments/quote', params),
    enabled,
    retry: false,
  });

export const usePackageOptions = (appointmentId: string | null) =>
  useQuery({
    queryKey: [FIN, 'package-options', appointmentId],
    queryFn: () => api.get<PackageOptionDto[]>('/payments/package-options', { appointmentId }),
    enabled: !!appointmentId,
  });

export const useSubscriptionInfo = (subscriptionId: string | null) =>
  useQuery({
    queryKey: [FIN, 'subscription-info', subscriptionId],
    queryFn: () => api.get<SubscriptionPaymentInfoDto>('/payments/subscription-info', { subscriptionId }),
    enabled: !!subscriptionId,
  });

// ---------- Despesas ----------
export const useExpenses = (query: Q) => useList<ExpenseDto>('/expenses', 'expenses', query);
export type ExpenseBody = {
  branchId?: string | null;
  category: string;
  description: string;
  amountCents: number;
  dueDate: string;
  paidAt?: string | null;
  cashRegisterId?: string | null;
};
export const useSaveExpense = () =>
  useFinMutation((b: { id?: string; body: ExpenseBody }) => (b.id ? api.put<ExpenseDto>(`/expenses/${b.id}`, b.body) : api.post<ExpenseDto>('/expenses', b.body)));
export const useCancelExpense = () => useFinMutation((id: string) => api.post<ExpenseDto>(`/expenses/${id}/cancel`));

// ---------- Comissões ----------
export const useCommissionRules = (enabled = true) =>
  useQuery({ queryKey: [FIN, 'rules'], queryFn: () => api.get<CommissionRuleDto[]>('/commission-rules'), enabled });
export type RuleBody = {
  barberId?: string | null;
  serviceId?: string | null;
  type: 'percent' | 'fixed';
  value: number;
  priority: number;
  active: boolean;
};
export const useSaveRule = () =>
  useFinMutation((b: { id?: string; body: RuleBody }) =>
    b.id ? api.put<CommissionRuleDto>(`/commission-rules/${b.id}`, b.body) : api.post<CommissionRuleDto>('/commission-rules', b.body),
  );
export const useDeleteRule = () => useFinMutation((id: string) => api.del<void>(`/commission-rules/${id}`));
export const useCommissions = (query: Q) =>
  useQuery({ queryKey: [FIN, 'commissions', query], queryFn: () => api.get<CommissionListDto>('/commissions', query), placeholderData: keepPreviousData });
export const usePayCommissions = () => useFinMutation((ids: string[]) => api.post<{ updated: number }>('/commissions/pay', { ids }));

// ---------- Relatórios ----------
export type ReportFilters = { from: string; to: string; branchId?: string; barberId?: string };
const useReport = <T,>(name: string, f: ReportFilters, enabled = true) =>
  useQuery({ queryKey: [FIN, 'report', name, f], queryFn: () => api.get<T>(`/reports/${name}`, f), enabled, placeholderData: keepPreviousData });
export const useSummary = (f: ReportFilters) => useReport<FinancialSummary>('summary', f);
export const useDaily = (f: ReportFilters) => useReport<DailyReportPoint[]>('daily', f);
export const useByBarber = (f: ReportFilters) => useReport<BarberReportRow[]>('by-barber', f);
export const useTopServices = (f: ReportFilters) => useReport<TopServiceRow[]>('top-services', f);
export const useByMethod = (f: ReportFilters) => useReport<PaymentMethodReportRow[]>('by-payment-method', f);

/** Serviços (id/nome) para selects de regra de comissão. */
export const useServiceOptions = () =>
  useQuery({
    queryKey: [FIN, 'service-options'],
    queryFn: () => api.get<Paginated<{ id: string; name: string }>>('/services', { pageSize: 100, sort: 'name', order: 'asc' }),
    staleTime: 300_000,
    select: (r) => r.data,
  });
